/**
 * Spoken names for city worlds and HD district stems.
 *
 * The registry stays English: attribution, ids, and photo filenames keep
 * `WorldSpec.place` and `HdDistrict.name`. The picker, the map menu, and the
 * gghere roster ask this module for the active locale and fall back to that
 * English string when a stem has no translation yet.
 */
import type { Locale } from '../../i18n/types.ts';

const LANGS = ['zh', 'zh-TW', 'ja', 'ko', 'th', 'nl', 'es', 'fr'] as const;
type Pack = readonly [string, string, string, string, string, string, string, string];

/** zh, zh-TW, ja, ko, th, nl, es, fr. English stays the registry string. */
const CITIES: Record<string, Pack> = {
  hk: ['香港', '香港', '香港', '홍콩', 'ฮ่องกง', 'Hongkong', 'Hong Kong', 'Hong Kong'],
  tokyo: ['东京', '東京', '東京', '도쿄', 'โตเกียว', 'Tokio', 'Tokio', 'Tokyo'],
  nyc: ['纽约', '紐約', 'ニューヨーク', '뉴욕', 'นิวยอร์ก', 'New York', 'Nueva York', 'New York'],
  paris: ['巴黎', '巴黎', 'パリ', '파리', 'ปารีส', 'Parijs', 'París', 'Paris'],
  amsterdam: ['阿姆斯特丹', '阿姆斯特丹', 'アムステルダム', '암스테르담', 'อัมสเตอร์ดัม', 'Amsterdam', 'Ámsterdam', 'Amsterdam'],
  rotterdam: ['鹿特丹', '鹿特丹', 'ロッテルダム', '로테르담', 'รอตเทอร์ดัม', 'Rotterdam', 'Róterdam', 'Rotterdam'],
  berlin: ['柏林', '柏林', 'ベルリン', '베를린', 'เบอร์ลิน', 'Berlijn', 'Berlín', 'Berlin'],
  sanfrancisco: ['旧金山', '舊金山', 'サンフランシスコ', '샌프란시스코', 'ซานฟรานซิสโก', 'San Francisco', 'San Francisco', 'San Francisco'],
  vienna: ['维也纳', '維也納', 'ウィーン', '빈', 'เวียนนา', 'Wenen', 'Viena', 'Vienne'],
  barcelona: ['巴塞罗那', '巴塞隆納', 'バルセロナ', '바르셀로나', 'บาร์เซโลนา', 'Barcelona', 'Barcelona', 'Barcelone'],
  copenhagen: ['哥本哈根', '哥本哈根', 'コペンハーゲン', '코펜하겐', 'โคเปนเฮเกน', 'Kopenhagen', 'Copenhague', 'Copenhague'],
  toronto: ['多伦多', '多倫多', 'トロント', '토론토', 'โทรอนโต', 'Toronto', 'Toronto', 'Toronto'],
  melbourne: ['墨尔本', '墨爾本', 'メルボルン', '멜버른', 'เมลเบิร์น', 'Melbourne', 'Melbourne', 'Melbourne'],
  edinburgh: ['爱丁堡', '愛丁堡', 'エディンバラ', '에든버러', 'เอดินบะระ', 'Edinburgh', 'Edimburgo', 'Édimbourg'],
  seoul: ['首尔', '首爾', 'ソウル', '서울', 'โซล', 'Seoel', 'Seúl', 'Séoul'],
  losangeles: ['洛杉矶', '洛杉磯', 'ロサンゼルス', '로스앤젤레스', 'ลอสแอนเจลิส', 'Los Angeles', 'Los Ángeles', 'Los Angeles'],
  dubai: ['迪拜', '杜拜', 'ドバイ', '두바이', 'ดูไบ', 'Dubai', 'Dubái', 'Dubaï'],
  bangkok: ['曼谷', '曼谷', 'バンコク', '방콕', 'กรุงเทพฯ', 'Bangkok', 'Bangkok', 'Bangkok'],
  capetown: ['开普敦', '開普敦', 'ケープタウン', '케이프타운', 'เคปทาวน์', 'Kaapstad', 'Ciudad del Cabo', 'Le Cap'],
  rio: ['里约热内卢', '里約熱內盧', 'リオデジャネイロ', '리우데자네이루', 'รีโอเดจาเนโร', 'Rio de Janeiro', 'Río de Janeiro', 'Rio de Janeiro'],
  istanbul: ['伊斯坦布尔', '伊斯坦堡', 'イスタンブール', '이스탄불', 'อิสตันบูล', 'Istanboel', 'Estambul', 'Istanbul'],
  venice: ['威尼斯', '威尼斯', 'ヴェネツィア', '베네치아', 'เวนิส', 'Venetië', 'Venecia', 'Venise'],
  shenzhen: ['深圳', '深圳', '深セン', '선전', 'เซินเจิ้น', 'Shenzhen', 'Shenzhen', 'Shenzhen'],
  taipei: ['台北', '臺北', '台北', '타이베이', 'ไทเป', 'Taipei', 'Taipéi', 'Taipei'],
  miami: ['迈阿密', '邁阿密', 'マイアミ', '마이애미', 'ไมอามี', 'Miami', 'Miami', 'Miami'],
  fukuoka: ['福冈', '福岡', '福岡', '후쿠오카', 'ฟุกุโอกะ', 'Fukuoka', 'Fukuoka', 'Fukuoka'],
  nagoya: ['名古屋', '名古屋', '名古屋', '나고야', 'นาโงยะ', 'Nagoya', 'Nagoya', 'Nagoya'],
  yokohama: ['横滨', '橫濱', '横浜', '요코하마', 'โยโกฮามะ', 'Yokohama', 'Yokohama', 'Yokohama'],
  osaka: ['大阪', '大阪', '大阪', '오사카', 'โอซากะ', 'Osaka', 'Osaka', 'Osaka'],
  athens: ['雅典', '雅典', 'アテネ', '아테네', 'เอเธนส์', 'Athene', 'Atenas', 'Athènes'],
  lisbon: ['里斯本', '里斯本', 'リスボン', '리스본', 'ลิสบอน', 'Lissabon', 'Lisboa', 'Lisbonne'],
  helsinki: ['赫尔辛基', '赫爾辛基', 'ヘルシンキ', '헬싱키', 'เฮลซิงกิ', 'Helsinki', 'Helsinki', 'Helsinki'],
  zurich: ['苏黎世', '蘇黎世', 'チューリッヒ', '취리히', 'ซูริก', 'Zürich', 'Zúrich', 'Zurich'],
  prague: ['布拉格', '布拉格', 'プラハ', '프라하', 'ปราก', 'Praag', 'Praga', 'Prague'],
  kyoto: ['京都', '京都', '京都', '교토', 'เกียวโต', 'Kyoto', 'Kioto', 'Kyoto'],
  vatican: ['梵蒂冈', '梵蒂岡', 'バチカン', '바티칸', 'นครวาติกัน', 'Vaticaanstad', 'Ciudad del Vaticano', 'Vatican'],
  monaco: ['摩纳哥', '摩納哥', 'モナコ', '모나코', 'โมนาโก', 'Monaco', 'Mónaco', 'Monaco'],
  macau: ['澳门', '澳門', 'マカオ', '마카오', 'มาเก๊า', 'Macau', 'Macao', 'Macao'],
  geneva: ['日内瓦', '日內瓦', 'ジュネーヴ', '제네바', 'เจนีวา', 'Genève', 'Ginebra', 'Genève'],
  singapore: ['新加坡', '新加坡', 'シンガポール', '싱가포르', 'สิงคโปร์', 'Singapore', 'Singapur', 'Singapour'],
  rome: ['罗马', '羅馬', 'ローマ', '로마', 'โรม', 'Rome', 'Roma', 'Rome'],
  london: ['伦敦', '倫敦', 'ロンドン', '런던', 'ลอนดอน', 'Londen', 'Londres', 'Londres'],
  jakarta: ['雅加达', '雅加達', 'ジャカルタ', '자카르타', 'จาการ์ตา', 'Jakarta', 'Yakarta', 'Jakarta'],
  mexicocity: ['墨西哥城', '墨西哥城', 'メキシコシティ', '멕시코시티', 'เม็กซิโกซิตี', 'Mexico-Stad', 'Ciudad de México', 'Mexico'],
  sanjose: ['圣何塞', '聖荷西', 'サンノゼ', '새너제이', 'ซานโฮเซ', 'San Jose', 'San José', 'San José'],
  telaviv: ['特拉维夫', '特拉維夫', 'テルアビブ', '텔아비브', 'เทลอาวีฟ', 'Tel Aviv', 'Tel Aviv', 'Tel-Aviv'],
  chicago: ['芝加哥', '芝加哥', 'シカゴ', '시카고', 'ชิคาโก', 'Chicago', 'Chicago', 'Chicago'],
  boston: ['波士顿', '波士頓', 'ボストン', '보스턴', 'บอสตัน', 'Boston', 'Boston', 'Boston'],
  mumbai: ['孟买', '孟買', 'ムンバイ', '뭄바이', 'มุมไบ', 'Mumbai', 'Bombay', 'Bombay'],
  moscow: ['莫斯科', '莫斯科', 'モスクワ', '모스크바', 'มอสโก', 'Moskou', 'Moscú', 'Moscou'],
  nanjing: ['南京', '南京', '南京', '난징', 'นานกิง', 'Nanjing', 'Nankín', 'Nankin'],
  beijing: ['北京', '北京', '北京', '베이징', 'ปักกิ่ง', 'Peking', 'Pekín', 'Pékin'],
  shanghai: ['上海', '上海', '上海', '상하이', 'เซี่ยงไฮ้', 'Shanghai', 'Shanghái', 'Shanghai'],
  sydney: ['悉尼', '雪梨', 'シドニー', '시드니', 'ซิดนีย์', 'Sydney', 'Sídney', 'Sydney'],
  kualalumpur: ['吉隆坡', '吉隆坡', 'クアラルンプール', '쿠알라룸푸르', 'กัวลาลัมเปอร์', 'Kuala Lumpur', 'Kuala Lumpur', 'Kuala Lumpur'],
  washingtondc: ['华盛顿', '華盛頓', 'ワシントン', '워싱턴', 'วอชิงตัน', 'Washington', 'Washington', 'Washington'],
  xian: ['西安', '西安', '西安', '시안', 'ซีอาน', "Xi'an", "Xi'an", "Xi'an"],
  zhengzhou: ['郑州', '鄭州', '鄭州', '정저우', 'เจิ้งโจว', 'Zhengzhou', 'Zhengzhou', 'Zhengzhou'],
  luoyang: ['洛阳', '洛陽', '洛陽', '뤄양', 'ลั่วหยาง', 'Luoyang', 'Luoyang', 'Luoyang'],
  sport: ['运动', '運動', 'スポーツ', '스포츠', 'กีฬา', 'Sport', 'Deporte', 'Sport'],
  games: ['游戏世界', '遊戲世界', 'ゲームの世界', '게임 월드', 'โลกเกม', 'Spelwerelden', 'Mundos de juego', 'Mondes de jeu'],
};

/** zh, zh-TW, ja, ko. Other locales keep the registry stem. */
type Stem4 = readonly [string, string, string, string];
const STEMS: Record<string, Stem4> = {
  Central: ['中环', '中環', '中環', '센트럴'],
  'Kennedy Town & HKU': ['坚尼地城与港大', '堅尼地城與港大', '堅尼地城・香港大学', '케네디타운·홍콩대'],
  'Central Waterfront': ['中环海滨', '中環海濱', '中環海浜', '센트럴 해안'],
  'Central Core': ['中环核心', '中環核心', '中環中心部', '센트럴 코어'],
  'TST Waterfront': ['尖东海滨', '尖東海濱', '尖沙咀海浜', '침사추이 해안'],
  'Mong Kok': ['旺角', '旺角', '旺角', '몽콕'],
  'Causeway Bay': ['铜锣湾', '銅鑼灣', '銅鑼湾', '코즈웨이베이'],
  'Kowloon Walled City': ['九龙寨城', '九龍寨城', '九龍城寨', '구룡성채'],
  'Wan Chai': ['湾仔', '灣仔', '湾仔', '완차이'],
  'Sheung Wan': ['上环', '上環', '上環', '셩완'],
  'Sham Shui Po': ['深水埗', '深水埗', '深水埗', '삼수이포'],
  'Yau Ma Tei': ['油麻地', '油麻地', '油麻地', '야우마테이'],
  'North Point': ['北角', '北角', '北角', '노스포인트'],
  'Kennedy Town': ['坚尼地城', '堅尼地城', '堅尼地城', '케네디타운'],
  Aberdeen: ['香港仔', '香港仔', '香港仔', '애버딘'],
  Stanley: ['赤柱', '赤柱', '赤柱', '스탠리'],
  'Hung Hom': ['红磡', '紅磡', '紅磡', '홍함'],
  'Tsim Sha Tsui': ['尖沙咀', '尖沙咀', '尖沙咀', '침사추이'],
  'Ngong Ping': ['昂坪', '昂坪', '昂坪', '옹핑'],
  'Kwun Tong': ['观塘', '觀塘', '観塘', '쿤통'],
  'Sai Kung': ['西贡', '西貢', '西貢', '사이쿵'],
  'The Peak': ['太平山顶', '太平山頂', '太平山', '더피크'],
  'Cheung Chau': ['长洲', '長洲', '長洲', '창저우'],
  'Tuen Mun': ['屯门', '屯門', '屯門', '툰문'],
  'HKUST Bay': ['科大海湾', '科大海灣', '香港科技大学の湾', '과기대 만'],
  'HK Island North': ['港岛北', '港島北', '香港島北部', '홍콩섬 북쪽'],
  'Hong Kong Island': ['香港岛', '香港島', '香港島', '홍콩섬'],
  'Hong Kong': ['香港', '香港', '香港', '홍콩'],
  'Central & Harbour': ['中环与海港', '中環與海港', '中環と港', '센트럴과 항구'],
  'Ocean Park': ['海洋公园', '海洋公園', '海洋公園', '오션파크'],
  'Repulse Bay': ['浅水湾', '淺水灣', '浅水湾', '리펄스베이'],
  'Kowloon Tong': ['九龙塘', '九龍塘', '九龍塘', '가우룽통'],
  'Kai Tak': ['启德', '啟德', '啓徳', '카이탁'],
  'Wong Tai Sin': ['黄大仙', '黃大仙', '黄大仙', '황대선'],
  'Sha Tin': ['沙田', '沙田', '沙田', '사틴'],
  'Tsuen Wan': ['荃湾', '荃灣', '荃湾', '취안완'],
  'Tsing Ma Bridge': ['青马大桥', '青馬大橋', '青馬大橋', '칭마대교'],
  'Yuen Long': ['元朗', '元朗', '元朗', '위엔롱'],
  'Tin Shui Wai': ['天水围', '天水圍', '天水囲', '톈수이웨이'],
  'Tai Po': ['大埔', '大埔', '大埔', '타이포'],
  'Fanling & Sheung Shui': ['粉岭与上水', '粉嶺與上水', '粉嶺・上水', '펀링·셩수이'],
  'Tseung Kwan O': ['将军澳', '將軍澳', '将軍澳', '장군오'],
  'Tung Chung': ['东涌', '東涌', '東涌', '둥충'],
  'HK Airport': ['香港机场', '香港機場', '香港国際空港', '홍콩국제공항'],
  'HK Disneyland': ['香港迪士尼', '香港迪士尼', '香港ディズニー', '홍콩 디즈니'],
  'Tian Tan Buddha': ['天坛大佛', '天壇大佛', '天壇大仏', '천단대불'],
  'HKU Campus': ['港大校园', '港大校園', '香港大学キャンパス', '홍콩대 캠퍼스'],
  'CUHK Hillside': ['中大山坡', '中大山坡', '中文大学の丘', '중문대 언덕'],
  'Victoria Harbour': ['维多利亚港', '維多利亞港', 'ビクトリア・ハーバー', '빅토리아 항'],
  Kowloon: ['九龙', '九龍', '九龍', '구룡'],
  'Hong Kong SAR': ['香港特别行政区', '香港特別行政區', '香港特別行政区', '홍콩특별행정구'],
  'Quarry Bay': ['鲗鱼涌', '鰂魚涌', '鰂魚涌', '쿼리베이'],
  'Ma On Shan': ['马鞍山', '馬鞍山', '馬鞍山', '마온산'],
  'Kwai Chung & Tsing Yi': ['葵涌与青衣', '葵涌與青衣', '葵涌・青衣', '콰이충·칭이'],
  Shinjuku: ['新宿', '新宿', '新宿', '신주쿠'],
  Shibuya: ['涩谷', '澀谷', '渋谷', '시부야'],
  Ginza: ['银座', '銀座', '銀座', '긴자'],
  Asakusa: ['浅草', '淺草', '浅草', '아사쿠사'],
  Ueno: ['上野', '上野', '上野', '우에노'],
  Ikebukuro: ['池袋', '池袋', '池袋', '이케부쿠로'],
  Akihabara: ['秋叶原', '秋葉原', '秋葉原', '아키하바라'],
  Roppongi: ['六本木', '六本木', '六本木', '롯폰기'],
  Odaiba: ['台场', '台場', 'お台場', '오다이바'],
  Shinagawa: ['品川', '品川', '品川', '시나가와'],
  Koenji: ['高圆寺', '高圓寺', '高円寺', '고엔지'],
  Kinshicho: ['锦糸町', '錦糸町', '錦糸町', '긴시초'],
  Toyosu: ['丰洲', '豐洲', '豊洲', '도요스'],
  Kichijoji: ['吉祥寺', '吉祥寺', '吉祥寺', '기치조지는'],
  Jiyugaoka: ['自由丘', '自由丘', '自由が丘', '지유가오카'],
  Tokyo: ['东京', '東京', '東京', '도쿄'],
  Gion: ['祇园', '祇園', '祇園', '기온'],
  Kawaramachi: ['河原町', '河原町', '河原町', '가와라마치'],
  'Kyoto Station': ['京都站', '京都站', '京都駅', '교토역'],
  'Fushimi Inari': ['伏见稻荷', '伏見稻荷', '伏見稲荷', '후시미이나리'],
  'Kinkaku-ji': ['金阁寺', '金閣寺', '金閣寺', '긴카쿠지'],
  'Ginkaku-ji': ['银阁寺', '銀閣寺', '銀閣寺', '긴카쿠지'],
  Kiyomizu: ['清水寺', '清水寺', '清水寺', '기요미즈'],
  Arashiyama: ['岚山', '嵐山', '嵐山', '아라시야마'],
  Umeda: ['梅田', '梅田', '梅田', '우메다'],
  Namba: ['难波', '難波', '難波', '난바'],
  Shinsaibashi: ['心斋桥', '心齋橋', '心斎橋', '신사이바시'],
  'Osaka Castle': ['大阪城', '大阪城', '大阪城', '오사카성'],
  'Qianmen & Dashilan': ['前门与大栅栏', '前門與大柵欄', '前門・大柵欄', '첸먼·다시란'],
  'Wangfujing & Dongdan': ['王府井与东单', '王府井與東單', '王府井・東単', '왕푸징·둥단'],
  'The Bund': ['外滩', '外灘', '外灘', '와이탄'],
  Lujiazui: ['陆家嘴', '陸家嘴', '陸家嘴', '루자쭈이'],
  Yuyuan: ['豫园', '豫園', '豫園', '위위안'],
  Xintiandi: ['新天地', '新天地', '新天地', '신톈디'],
  Taipei: ['台北', '臺北', '台北', '타이베이'],
  'Taipei Main': ['台北车站', '臺北車站', '台北駅', '타이베이역'],
  Ximending: ['西门町', '西門町', '西門町', '시먼딩'],
  Xinyi: ['信义', '信義', '信義', '신이'],
  Shilin: ['士林', '士林', '士林', '스린'],
  Beitou: ['北投', '北投', '北投', '베이터우'],
  Seoul: ['首尔', '首爾', 'ソウル', '서울'],
  Gwanghwamun: ['光化门', '光化門', '光化門', '광화문'],
  Bukchon: ['北村', '北村', '北村', '북촌'],
  Gangnam: ['江南', '江南', '江南', '강남'],
  'Île de la Cité': ['西岱岛', '西岱島', 'シテ島', '시테섬'],
  Louvre: ['卢浮宫', '羅浮宮', 'ルーヴル', '루브르'],
  'Le Marais': ['玛莱区', '瑪萊區', 'マレ地区', '마레'],
  'Latin Quarter': ['拉丁区', '拉丁區', 'カルチェ・ラタン', '라틴 지구'],
  Montmartre: ['蒙马特', '蒙馬特', 'モンマルトル', '몽마르트'],
  'La Défense': ['拉德芳斯', '拉德芳斯', 'ラ・デファンス', '라데팡스'],
  Paris: ['巴黎', '巴黎', 'パリ', '파리'],
  Soho: ['苏活', '蘇活', 'ソーホー', '소호'],
  Westminster: ['威斯敏斯特', '威斯敏斯特', 'ウェストミンスター', '웨스트민스터'],
  'Canary Wharf': ['金丝雀码头', '金絲雀碼頭', 'カナリー・ワーフ', '카나리워프'],
  Greenwich: ['格林尼治', '格林尼治', 'グリニッジ', '그리니치'],
  'Lower Manhattan': ['下曼哈顿', '下曼哈頓', 'ロウアー・マンハッタン', '로어맨해튼'],
  Midtown: ['中城', '中城', 'ミッドタウン', '미드타운'],
  'Central Park': ['中央公园', '中央公園', 'セントラル・パーク', '센트럴파크'],
  Harlem: ['哈莱姆', '哈萊姆', 'ハーレム', '할렘'],
  'East Village': ['东村', '東村', 'イースト・ヴィレッジ', '이스트빌리지'],
  'Greenwich Village': ['格林尼治村', '格林尼治村', 'グリニッジ・ヴィレッジ', '그리니치빌리지'],
  'New York': ['纽约', '紐約', 'ニューヨーク', '뉴욕'],
};

export const PLACE_LOCALES = LANGS;

export function worldPlace(id: string, locale: Locale, english: string): string {
  if (locale === 'en') return english;
  const pack = CITIES[id];
  if (!pack) return english;
  const index = LANGS.indexOf(locale as (typeof LANGS)[number]);
  return index < 0 ? english : pack[index];
}

export function worldPlaceAliases(id: string, english: string): readonly string[] {
  const pack = CITIES[id];
  return pack ? [english, ...pack] : [english];
}

const KINDS: Record<string, Stem4> = {
  Ultra: ['超清', '超清', '超精細', '울트라'],
  'Cube-Belt': ['立方带', '立方帶', 'キューブベルト', '큐브 벨트'],
  'Whole-City Cube': ['整座城市', '整座城市', '都市全体', '도시 전체'],
  'Ultra-Belt': ['超清带', '超清帶', '超精細ベルト', '울트라 벨트'],
  Cube: ['立方', '立方', 'キューブ', '큐브'],
  Globe: ['全球', '全球', '地球儀', '지구'],
  'Whole Planet': ['整颗星球', '整顆星球', '惑星全体', '행성 전체'],
  Meta: ['总览', '總覽', '全体', '전체'],
};

export function stemLabel(stem: string, locale: Locale): string {
  if (locale === 'en') return stem;
  const pack = KINDS[stem] ?? STEMS[stem];
  if (!pack) return stem;
  const index = ['zh', 'zh-TW', 'ja', 'ko'].indexOf(locale);
  return index < 0 ? stem : pack[index];
}

/** Translate each " · " piece. Bake suffixes stay in the string, in the active language. */
export function localizePieces(name: string, locale: Locale): string {
  return name
    .split(' · ')
    .map((part) => stemLabel(part.trim(), locale))
    .join(' · ');
}

export function stemAliases(stem: string): readonly string[] {
  const pack = STEMS[stem];
  return pack ? [stem, ...pack] : [stem];
}

/** Card label: drop the English bake suffix, then translate each remaining piece. */
export function districtCardName(name: string, locale: Locale): string {
  const stripped = name.replace(' · Ultra', '').replace(' · Cube-Belt', '');
  return stripped
    .split(' · ')
    .map((part) => stemLabel(part.trim(), locale))
    .join(' · ');
}

export function districtHaystack(name: string, id: string): string {
  const stem = name.split(' · ')[0]?.trim() ?? name;
  return [name, id, ...stemAliases(stem)].join(' ').toLowerCase();
}

export function hasCityPack(id: string): boolean {
  return id in CITIES;
}
